import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Loader2, X, Link2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * DhanConnectModal — collect the user's own DHAN client_id + access_token.
 *
 * Why we ask the user for their own creds:
 * - Trades route through the user's OWN DHAN account (not ours).
 * - DHAN doesn't have OAuth — credentials are manual from DHAN dashboard.
 * - We verify before saving (POST /v2/fundlimit) so bad creds fail loudly.
 *
 * Stored server-side in MongoDB keyed by (user_id, broker="dhan"). Never
 * exposed back to the client after save.
 */
export default function DhanConnectModal({ open, onClose, onConnected }) {
  const [clientId, setClientId] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [proxyUrl, setProxyUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
  }, [open]);

  const submit = async () => {
    if (!clientId.trim() || !accessToken.trim()) {
      toast.error("Both client ID and access token are required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/dhan/connect", {
        client_id: clientId.trim(),
        access_token: accessToken.trim(),
        proxy_url: proxyUrl.trim() || undefined,
      });
      toast.success(`DHAN connected · client ${data.client_id}`);
      setClientId("");
      setAccessToken("");
      onConnected?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connect failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="dhan-connect-modal">
      <div className="bg-[#0a0a0a] border border-zinc-800 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono">Connect Broker</div>
            <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-white">DHAN HQ</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="dhan-modal-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Why DHAN explainer */}
          <div className="text-xs text-zinc-400 leading-relaxed">
            DHAN allows true one-click &ldquo;Place all legs&rdquo; execution from ProTradeIQ. Your DHAN credentials stay in your account; we just relay your orders.
          </div>

          {/* New to DHAN? Referral + guide */}
          <div className="border border-[#22C55E]/30 bg-[#22C55E]/5 p-3 space-y-1.5" data-testid="dhan-referral-box">
            <div className="text-[10px] uppercase tracking-widest text-[#22C55E] font-mono">New to DHAN?</div>
            <div className="text-xs text-zinc-300 leading-relaxed">Open a free DHAN account in ~10 minutes (PAN + Aadhaar, fully online), then come back here to connect.</div>
            <div className="flex flex-wrap gap-3 pt-1">
              <a href="https://join.dhan.co/?invite=EVMCD04986" target="_blank" rel="noreferrer"
                 className="text-[11px] text-[#22C55E] hover:underline font-mono font-bold uppercase tracking-widest inline-flex items-center gap-1"
                 data-testid="dhan-open-account-link">
                <ExternalLink className="h-2.5 w-2.5" /> Open DHAN account
              </a>
              <a href="/dhan-guide" className="text-[11px] text-[#3B9BFF] hover:underline font-mono uppercase tracking-widest" data-testid="dhan-howto-link">
                How to connect — full guide →
              </a>
            </div>
          </div>

          {/* Static IP requirement (SEBI) */}
          <div className="border border-[#3B9BFF]/30 bg-[#3B9BFF]/5 p-3 space-y-1.5" data-testid="static-ip-box">
            <div className="text-[10px] uppercase tracking-widest text-[#3B9BFF] font-mono font-bold">Static IP — required by SEBI</div>
            <div className="text-xs text-zinc-300 leading-relaxed">
              SEBI's algo-trading rules require every DHAN account to whitelist a <b>dedicated static IP</b> for API orders
              (one IP = one client). Home broadband IPs change daily — you need a personal static-IP relay:
            </div>
            <ol className="text-xs text-zinc-300 leading-relaxed space-y-1 list-decimal pl-4">
              <li>Get your own static IP relay at <a href="https://staticip.in/signup?ref=PROTRADEIQ" target="_blank" rel="noreferrer" className="text-[#3B9BFF] hover:underline font-bold" data-testid="staticip-referral-link">staticip.in</a> (~₹400/mo, 5-min setup)</li>
              <li>Note the static IP they assign you (e.g. 34.55.xx.xx)</li>
              <li>In DHAN: <b>My Profile → DhanHQ APIs → Whitelisted IPs</b> → add that IP as Primary</li>
              <li>Generate your access token AFTER whitelisting</li>
              <li>Copy your <b>relay URL</b> from the staticip.in dashboard (format <code className="text-[#3B9BFF]">http://user:pass@host:port</code>) and paste it in the relay field below — we verify it live with DHAN before saving</li>
            </ol>
            <div className="text-[10px] text-[#F59E0B] leading-relaxed font-mono border-l-2 border-[#F59E0B]/60 pl-2" data-testid="static-ip-warning">
              One relay = one DHAN client. Never borrow or share a relay URL — DHAN rejects orders (DH-905 Invalid IP) and SEBI treats a shared IP as a compliance breach.
            </div>
            <a href="https://staticip.in/signup?ref=PROTRADEIQ" target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 text-[11px] text-[#3B9BFF] hover:underline font-mono font-bold uppercase tracking-widest pt-1">
              <ExternalLink className="h-2.5 w-2.5" /> Get your static IP →
            </a>
          </div>

          {/* How to get creds */}
          <div className="border border-zinc-800 bg-[#121212] p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">How to get these</div>
            <ol className="text-xs text-zinc-300 leading-relaxed space-y-1 list-decimal pl-4">
              <li>Log in at <a href="https://web.dhan.co/" target="_blank" rel="noreferrer" className="text-[#007AFF] hover:underline">web.dhan.co</a></li>
              <li>Open <b>My Profile → DhanHQ APIs</b></li>
              <li>
                <b>Whitelist YOUR static IP</b> (from the staticip.in step above) under Whitelisted IPs — SEBI allows one static IP per client, so it must be your own</li>
              <li>Click <b>Generate Access Token</b> (valid 24h or as set) — generate it AFTER whitelisting the IP</li>
              <li>Copy your <b>Client ID</b> + the generated <b>Access Token</b></li>
            </ol>
            <a href="https://dhanhq.co/docs/v2/" target="_blank" rel="noreferrer"
               className="text-[10px] text-[#007AFF] hover:underline font-mono inline-flex items-center gap-1">
              <ExternalLink className="h-2.5 w-2.5" /> DHAN API docs
            </a>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">DHAN Client ID</label>
              <input
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="e.g. 1100123456"
                className="mt-1 w-full bg-[#0a0a0a] border border-zinc-800 focus:border-[#007AFF] px-3 py-2 text-sm font-mono outline-none"
                data-testid="dhan-client-id-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Access Token</label>
              <textarea
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                placeholder="Paste the JWT-style access token from DHAN dashboard…"
                rows={4}
                className="mt-1 w-full bg-[#0a0a0a] border border-zinc-800 focus:border-[#007AFF] px-3 py-2 text-xs font-mono outline-none resize-none"
                data-testid="dhan-token-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Your static IP relay URL <span className="text-[#F59E0B] normal-case">(required for order execution & auto-trade)</span></label>
              <input
                value={proxyUrl}
                onChange={(e) => setProxyUrl(e.target.value)}
                placeholder="http://user:pass@dc-mum-xxx.staticip.in:443"
                className="mt-1 w-full bg-[#0a0a0a] border border-zinc-800 focus:border-[#3B9BFF] px-3 py-2 text-xs font-mono outline-none"
                data-testid="dhan-proxy-input"
              />
              <div className="mt-1 text-[9px] text-zinc-600 font-mono">Your orders will exit via YOUR own static IP (the one you whitelisted in DHAN). Required for order execution & auto-trade.</div>
            </div>
          </div>

          {/* Reassurance */}
          <div className="text-[10px] text-zinc-500 leading-relaxed font-mono uppercase tracking-widest border-l-2 border-zinc-800 pl-3">
            Credentials encrypted at rest · only used to relay your own orders · never shared.
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={onClose}
              className="flex-1 text-xs uppercase tracking-widest border border-zinc-800 hover:border-zinc-600 text-zinc-400 hover:text-white py-2.5 font-mono"
              data-testid="dhan-modal-cancel"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={submitting || !clientId.trim() || !accessToken.trim()}
              className="flex-[2] text-xs uppercase tracking-widest border border-[#22C55E] bg-[#22C55E]/10 hover:bg-[#22C55E]/20 text-[#22C55E] disabled:opacity-50 py-2.5 font-mono font-bold flex items-center justify-center gap-2"
              data-testid="dhan-modal-submit"
            >
              {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
              {submitting ? "Verifying with DHAN…" : "Verify & Connect"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
